import {
  combineLatestWith,
  distinctUntilChanged,
  firstValueFrom,
  map,
  Observable,
  shareReplay,
} from "rxjs";

import { ApiService } from "@bitwarden/common/abstractions/api.service";
import { PolicyService } from "@bitwarden/common/admin-console/abstractions/policy/policy.service.abstraction";
import { AccountService } from "@bitwarden/common/auth/abstractions/account.service";
import { TokenService } from "@bitwarden/common/auth/abstractions/token.service";
import { AuthenticationType } from "@bitwarden/common/auth/enums/authentication-type";
import { AuthResult } from "@bitwarden/common/auth/models/domain/auth-result";
import { TokenTwoFactorRequest } from "@bitwarden/common/auth/models/request/identity-token/token-two-factor.request";
import { PasswordPreloginService } from "@bitwarden/common/auth/password-prelogin";
import { TwoFactorService } from "@bitwarden/common/auth/two-factor";
import { BillingAccountProfileStateService } from "@bitwarden/common/billing/abstractions/account/billing-account-profile-state.service";
import { AccountCryptographicStateService } from "@bitwarden/common/key-management/account-cryptography/account-cryptographic-state.service";
import { DeviceTrustServiceAbstraction } from "@bitwarden/common/key-management/device-trust/abstractions/device-trust.service.abstraction";
import { KeyConnectorService } from "@bitwarden/common/key-management/key-connector/abstractions/key-connector.service";
import { InternalMasterPasswordServiceAbstraction } from "@bitwarden/common/key-management/master-password/abstractions/master-password.service.abstraction";
import { VaultTimeoutSettingsService } from "@bitwarden/common/key-management/vault-timeout";
import { ErrorResponse } from "@bitwarden/common/models/response/error.response";
import { AppIdService } from "@bitwarden/common/platform/abstractions/app-id.service";
import { ConfigService } from "@bitwarden/common/platform/abstractions/config/config.service";
import { EnvironmentService } from "@bitwarden/common/platform/abstractions/environment.service";
import { I18nService } from "@bitwarden/common/platform/abstractions/i18n.service";
import { LogService } from "@bitwarden/common/platform/abstractions/log.service";
import { MessagingService } from "@bitwarden/common/platform/abstractions/messaging.service";
import { PlatformUtilsService } from "@bitwarden/common/platform/abstractions/platform-utils.service";
import { GlobalState, GlobalStateProvider } from "@bitwarden/common/platform/state";
import { PasswordStrengthServiceAbstraction } from "@bitwarden/common/tools/password-strength";
import { KeyService, KdfConfigService } from "@bitwarden/key-management";
// eslint-disable-next-line no-restricted-imports
import { EncryptService, LegacyCompatKeyService } from "@bitwarden/legacy-crypto";
import { UnlockService } from "@bitwarden/unlock";

import { AuthRequestServiceAbstraction, LoginStrategyServiceAbstraction } from "../../abstractions";
import { LoginStrategyCacheService } from "../../abstractions/login-strategy-cache.service";
import { LoginStrategySessionTimeoutService } from "../../abstractions/login-strategy-session-timeout.service";
import { InternalUserDecryptionOptionsServiceAbstraction } from "../../abstractions/user-decryption-options.service.abstraction";
import { AuthRequestLoginStrategy } from "../../login-strategies/auth-request-login.strategy";
import { LoginStrategy } from "../../login-strategies/login.strategy";
import { PasswordLoginStrategy } from "../../login-strategies/password-login.strategy";
import { SsoLoginStrategy } from "../../login-strategies/sso-login.strategy";
import { UserApiLoginStrategy } from "../../login-strategies/user-api-login.strategy";
import { WebAuthnLoginStrategy } from "../../login-strategies/webauthn-login.strategy";
import {
  UserApiLoginCredentials,
  PasswordLoginCredentials,
  SsoLoginCredentials,
  AuthRequestLoginCredentials,
  WebAuthnLoginCredentials,
} from "../../models";

import { AUTH_REQUEST_PUSH_NOTIFICATION_KEY, CacheData } from "./login-strategy.state";

export class LoginStrategyService implements LoginStrategyServiceAbstraction {
  private authRequestPushNotificationState: GlobalState<string | null>;

  private loginStrategy$: Observable<
    | UserApiLoginStrategy
    | PasswordLoginStrategy
    | SsoLoginStrategy
    | AuthRequestLoginStrategy
    | WebAuthnLoginStrategy
    | null
  >;

  currentAuthType$: Observable<AuthenticationType | null>;

  constructor(
    private accountService: AccountService,
    private masterPasswordService: InternalMasterPasswordServiceAbstraction,
    private keyService: KeyService,
    private apiService: ApiService,
    private tokenService: TokenService,
    private appIdService: AppIdService,
    private platformUtilsService: PlatformUtilsService,
    private messagingService: MessagingService,
    private logService: LogService,
    private keyConnectorService: KeyConnectorService,
    private environmentService: EnvironmentService,
    private twoFactorService: TwoFactorService,
    private i18nService: I18nService,
    private encryptService: EncryptService,
    private passwordStrengthService: PasswordStrengthServiceAbstraction,
    private policyService: PolicyService,
    private deviceTrustService: DeviceTrustServiceAbstraction,
    private authRequestService: AuthRequestServiceAbstraction,
    private userDecryptionOptionsService: InternalUserDecryptionOptionsServiceAbstraction,
    private stateProvider: GlobalStateProvider,
    private billingAccountProfileStateService: BillingAccountProfileStateService,
    private vaultTimeoutSettingsService: VaultTimeoutSettingsService,
    private kdfConfigService: KdfConfigService,
    private configService: ConfigService,
    private accountCryptographicStateService: AccountCryptographicStateService,
    private passwordPreloginService: PasswordPreloginService,
    private unlockService: UnlockService,
    private loginStrategyCacheService: LoginStrategyCacheService,
    private loginStrategySessionTimeoutService: LoginStrategySessionTimeoutService,
    private legacyCompatKeyService: LegacyCompatKeyService,
  ) {
    this.authRequestPushNotificationState = this.stateProvider.get(
      AUTH_REQUEST_PUSH_NOTIFICATION_KEY,
    );

    this.currentAuthType$ = this.loginStrategyCacheService.currentAuthType$;
    this.loginStrategy$ = this.loginStrategyCacheService.currentAuthType$.pipe(
      distinctUntilChanged(),
      combineLatestWith(this.loginStrategyCacheService.cacheData$),
      this.initializeLoginStrategy.bind(this),
      shareReplay({ refCount: true, bufferSize: 1 }),
    );
  }

  async getEmail(): Promise<string | undefined> {
    const strategy = await firstValueFrom(this.loginStrategy$);

    if (strategy && "email$" in strategy) {
      return await firstValueFrom(strategy.email$);
    }
    return undefined;
  }

  async getMasterPasswordHash(): Promise<string | undefined> {
    const strategy = await firstValueFrom(this.loginStrategy$);

    if (strategy && "serverMasterKeyHash$" in strategy) {
      return await firstValueFrom(strategy.serverMasterKeyHash$);
    }
    return undefined;
  }

  async getSsoEmail2FaSessionToken(): Promise<string | undefined> {
    const strategy = await firstValueFrom(this.loginStrategy$);

    if (strategy && "ssoEmail2FaSessionToken$" in strategy) {
      return await firstValueFrom(strategy.ssoEmail2FaSessionToken$);
    }
    return undefined;
  }

  async getAccessCode(): Promise<string | undefined> {
    const strategy = await firstValueFrom(this.loginStrategy$);

    if (strategy && "accessCode$" in strategy) {
      return await firstValueFrom(strategy.accessCode$);
    }
    return undefined;
  }

  async getAuthRequestId(): Promise<string | undefined> {
    const strategy = await firstValueFrom(this.loginStrategy$);

    if (strategy && "authRequestId$" in strategy) {
      return await firstValueFrom(strategy.authRequestId$);
    }
    return undefined;
  }

  async logIn(
    credentials:
      | UserApiLoginCredentials
      | PasswordLoginCredentials
      | SsoLoginCredentials
      | AuthRequestLoginCredentials
      | WebAuthnLoginCredentials,
  ): Promise<AuthResult> {
    await this.clearCache();

    await this.loginStrategyCacheService.setCurrentAuthType(credentials.type);

    const strategy = await firstValueFrom(this.loginStrategy$);

    // Note: We aren't passing the credentials directly to the strategy since they are
    // created in the popup and can cause DeadObject references on Firefox.
    // This is a shallow copy, but use deep copy in future if objects are added to credentials
    // that were created in popup.
    // If the popup uses its own instance of this service, this can be removed.
    const ownedCredentials = { ...credentials };

    let result: AuthResult | undefined;
    try {
      result = await strategy?.logIn(ownedCredentials as any);
    } catch (e) {
      // A failed attempt must not leave an auth type behind: the auth guards treat a set auth
      // type as a login in progress.
      await this.clearCache();
      throw e;
    }

    if (result != null && !result.requiresTwoFactor && !result.requiresDeviceVerification) {
      await this.clearCache();
    } else {
      // Cache the strategy data so we can attempt again later with 2fa or device verification
      await this.loginStrategyCacheService.setCacheData(strategy?.exportCache() ?? null);
      await this.loginStrategySessionTimeoutService.startSessionTimeout();
    }

    if (!result) {
      throw new Error("No auth result returned");
    }
    return result;
  }

  async logInTwoFactor(twoFactor: TokenTwoFactorRequest): Promise<AuthResult> {
    if (!(await this.isSessionValid())) {
      throw new Error(this.i18nService.t("sessionTimeout"));
    }

    const strategy = await firstValueFrom(this.loginStrategy$);
    if (strategy == null) {
      throw new Error("No login strategy found.");
    }

    try {
      const result = await strategy.logInTwoFactor(twoFactor);

      // Only clear cache if 2FA token has been accepted, otherwise we need to be able to try again
      if (result != null && !result.requiresTwoFactor) {
        await this.clearCache();
      }
      return result;
    } catch (e) {
      // API exceptions are okay, but if there are any unhandled client-side errors then clear cache to be safe
      if (!(e instanceof ErrorResponse)) {
        await this.clearCache();
      }
      throw e;
    }
  }

  /**
   * Sends a token request to the server with the provided device verification OTP.
   * Returns an error if no session data is found or if the current login strategy does not support device verification.
   * @param deviceVerificationOtp The OTP to send to the server for device verification.
   * @returns The result of the token request.
   */
  async logInNewDeviceVerification(deviceVerificationOtp: string): Promise<AuthResult> {
    if (!(await this.isSessionValid())) {
      throw new Error(this.i18nService.t("sessionTimeout"));
    }

    const strategy = await firstValueFrom(this.loginStrategy$);
    if (strategy == null) {
      throw new Error("No login strategy found.");
    }

    if (!("logInNewDeviceVerification" in strategy)) {
      throw new Error("Current login strategy does not support device verification.");
    }

    try {
      const result = await strategy.logInNewDeviceVerification(deviceVerificationOtp);

      // Only clear cache if device verification succeeds
      if (result !== null && !result.requiresDeviceVerification) {
        await this.clearCache();
      }
      return result;
    } catch (e) {
      // Clear the cache if there is an unhandled client-side error
      if (!(e instanceof ErrorResponse)) {
        await this.clearCache();
      }
      throw e;
    }
  }

  private async clearCache(): Promise<void> {
    await this.loginStrategyCacheService.clearCache();
    await this.loginStrategySessionTimeoutService.cancelSessionTimeout();
  }

  private async isSessionValid(): Promise<boolean> {
    const cache = await firstValueFrom(this.loginStrategyCacheService.cacheData$);
    if (cache == null) {
      return false;
    }

    // If the Login Strategy Cache Expiration State value is less than the current
    // datetime stamp, then the cache is invalid and should be cleared.
    const expiration = await firstValueFrom(this.loginStrategyCacheService.cacheExpiration$);
    if (expiration != null && expiration < new Date()) {
      await this.clearCache();
      return false;
    }
    return true;
  }

  private initializeLoginStrategy(
    source: Observable<[AuthenticationType | null, CacheData | null]>,
  ) {
    const sharedDeps: ConstructorParameters<typeof LoginStrategy> = [
      this.accountService,
      this.masterPasswordService,
      this.keyService,
      this.encryptService,
      this.apiService,
      this.tokenService,
      this.appIdService,
      this.platformUtilsService,
      this.messagingService,
      this.logService,
      this.twoFactorService,
      this.userDecryptionOptionsService,
      this.billingAccountProfileStateService,
      this.vaultTimeoutSettingsService,
      this.kdfConfigService,
      this.environmentService,
      this.configService,
      this.accountCryptographicStateService,
    ];

    return source.pipe(
      map(([strategy, data]) => {
        if (strategy == null) {
          return null;
        }
        switch (strategy) {
          case AuthenticationType.Password:
            return new PasswordLoginStrategy(
              data?.password,
              this.passwordStrengthService,
              this.policyService,
              this.passwordPreloginService,
              this.unlockService,
              this.legacyCompatKeyService,
              ...sharedDeps,
            );
          case AuthenticationType.Sso:
            return new SsoLoginStrategy(
              data?.sso,
              this.keyConnectorService,
              this.unlockService,
              this.deviceTrustService,
              this.authRequestService,
              ...sharedDeps,
            );
          case AuthenticationType.UserApiKey:
            return new UserApiLoginStrategy(data?.userApiKey, this.unlockService, ...sharedDeps);
          case AuthenticationType.AuthRequest:
            return new AuthRequestLoginStrategy(
              data?.authRequest,
              this.unlockService,
              this.deviceTrustService,
              ...sharedDeps,
            );
          case AuthenticationType.WebAuthn:
            return new WebAuthnLoginStrategy(data?.webAuthn, this.unlockService, ...sharedDeps);
        }
      }),
    );
  }
}
